package com.arenaproplay.admin.ui.theme

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.px

fun initAppTheme(ctx: InitSilkContext) {

    installGlobalErrorOverlay()

    val googleFontsLink = document.createElement("link") as org.w3c.dom.HTMLLinkElement
    googleFontsLink.rel = "stylesheet"
    googleFontsLink.href =
        "https://fonts.googleapis.com/css2?" +
            "family=Unbounded:wght@500;600;700&" +
            "family=Inter:wght@300;400;500;600;700;800&" +
            "family=JetBrains+Mono:wght@400;500;600;700&display=swap"
    document.head?.appendChild(googleFontsLink)

    val preconnect = document.createElement("link") as org.w3c.dom.HTMLLinkElement
    preconnect.rel = "preconnect"
    preconnect.href = "https://fonts.googleapis.com"
    document.head?.insertBefore(preconnect, document.head?.firstChild)

    val preconnectGstatic = document.createElement("link") as org.w3c.dom.HTMLLinkElement
    preconnectGstatic.rel = "preconnect"
    preconnectGstatic.href = "https://fonts.gstatic.com"
    preconnectGstatic.setAttribute("crossorigin", "")
    document.head?.insertBefore(preconnectGstatic, document.head?.firstChild)

    val styleEl = document.createElement("style")
    styleEl.textContent = """
        @keyframes arena-rise {
            from { opacity: 0; transform: translateY(8px); }
            to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes arena-blink {
            0%, 60%   { opacity: 1; }
            61%, 100% { opacity: 0.25; }
        }
        @keyframes arena-pulse {
            0%, 100% { opacity: 0.6; transform: scale(1); }
            50%      { opacity: 1;   transform: scale(1.4); }
        }
        @keyframes admin-spinner {
            0%   { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
        @keyframes admin-skeleton {
            0%, 100% { opacity: 1; }
            50%      { opacity: 0.45; }
        }
        .arena-chat-grid {
            display: grid;
            grid-template-columns: minmax(280px, 340px) 1fr;
            gap: 16px;
            align-items: start;
        }
        @media (max-width: 1000px) {
            .arena-chat-grid { grid-template-columns: 1fr; }
        }
        ::-webkit-scrollbar { width: 10px; height: 10px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb {
            background: rgba(240, 244, 252, 0.14);
            border-radius: 10px;
            border: 2px solid transparent;
            background-clip: padding-box;
        }
        ::-webkit-scrollbar-thumb:hover { background: rgba(240, 244, 252, 0.26); background-clip: padding-box; border: 2px solid transparent; }

        .num { font-feature-settings: "tnum" 1, "ss01" 1; font-variant-numeric: tabular-nums; }
        .display-font { font-family: 'Unbounded', 'Inter', sans-serif; letter-spacing: -0.02em; }
        .heading-font { font-family: 'Unbounded', sans-serif; letter-spacing: -0.02em; }
        .mono-font { font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .na-num { font-family: 'Unbounded', 'Inter', sans-serif; letter-spacing: -0.02em; font-feature-settings: "tnum" 1; font-variant-numeric: tabular-nums; }

        /* ── Night Arena scene (kontaq formula in Arena colors) ─────────── */
        .na-scene { position: fixed; inset: 0; z-index: 0; pointer-events: none; overflow: hidden; }
        .na-scene i { position: absolute; border-radius: 50%; filter: blur(8px); }
        .na-scene i:nth-of-type(1) { left: -320px; top: -280px; width: 720px; height: 720px; background: radial-gradient(circle, rgba(0,97,255,.22) 0%, rgba(0,97,255,.08) 40%, transparent 68%); }
        .na-scene i:nth-of-type(2) { right: -300px; top: 60px; width: 600px; height: 600px; background: radial-gradient(circle, rgba(0,229,164,.10) 0%, rgba(0,229,164,.04) 42%, transparent 68%); }
        .na-scene i:nth-of-type(3) { left: 30vw; bottom: -360px; width: 640px; height: 640px; background: radial-gradient(circle, rgba(228,205,64,.07) 0%, transparent 66%); }
        .na-grid { position: absolute; inset: -60px 0; background-image: linear-gradient(rgba(255,255,255,.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.03) 1px, transparent 1px); background-size: 58px 58px; mask-image: radial-gradient(90% 70% at 50% 30%, #000 30%, transparent 100%); -webkit-mask-image: radial-gradient(90% 70% at 50% 30%, #000 30%, transparent 100%); }
        .na-noise { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: .35; background-image: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E"); }

        /* ── Glass primitives ───────────────────────────────────────────── */
        .na-glass {
            background: rgba(255, 255, 255, 0.04);
            border: 1px solid rgba(255, 255, 255, 0.08);
            backdrop-filter: blur(14px);
            -webkit-backdrop-filter: blur(14px);
            transition: background 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
        }
        .na-glass-hover:hover {
            background: rgba(255, 255, 255, 0.06);
            border-color: rgba(255, 255, 255, 0.14);
            box-shadow: 0 16px 40px -12px rgba(0, 0, 0, 0.55);
        }

        /* ── Micro-interactions ─────────────────────────────────────────── */
        .na-press { transition: transform 120ms ease; }
        .na-press:active { transform: scale(0.96); }
        @keyframes na-pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(0, 229, 164, 0.5); }
            50%      { box-shadow: 0 0 0 7px rgba(0, 229, 164, 0); }
        }
        .na-pulse { border-radius: 50%; animation: na-pulse 1.7s ease-in-out infinite; }

        @keyframes na-fade-in {
            from { opacity: 0; transform: translateY(4px); }
            to   { opacity: 1; transform: translateY(0); }
        }
        .na-fade-in { animation: na-fade-in 200ms cubic-bezier(0.16, 1, 0.3, 1) both; }

        /* ── World map choropleth (kontaq QpWorldMap technique, NA blue) ── */
        .na-map svg { display: block; width: 100%; height: auto; }
        .na-map path {
            fill: rgba(255,255,255,.06);
            stroke: rgba(5,8,15,.9);
            stroke-width: .4;
            transition: fill .2s;
        }
        .na-map path:hover { fill: #4D8CFF !important; }

        .arena-rise > * { animation: arena-rise 420ms cubic-bezier(0.16, 1, 0.3, 1) both; }
        .arena-rise > *:nth-child(1) { animation-delay: 40ms; }
        .arena-rise > *:nth-child(2) { animation-delay: 90ms; }
        .arena-rise > *:nth-child(3) { animation-delay: 140ms; }
        .arena-rise > *:nth-child(4) { animation-delay: 190ms; }
        .arena-rise > *:nth-child(5) { animation-delay: 240ms; }
        .arena-rise > *:nth-child(6) { animation-delay: 290ms; }
        .arena-rise > *:nth-child(7) { animation-delay: 340ms; }
        .arena-rise > *:nth-child(8) { animation-delay: 390ms; }

        .soft-card {
            background: rgba(255, 255, 255, 0.04);
            border-radius: 12px;
            border: 1px solid rgba(255, 255, 255, 0.08);
            backdrop-filter: blur(14px);
            -webkit-backdrop-filter: blur(14px);
        }
        .soft-card-tinted {
            background: rgba(255, 255, 255, 0.03);
            border-radius: 12px;
            border: 1px solid rgba(255, 255, 255, 0.06);
        }
        .feature-card {
            background: linear-gradient(135deg, #0E1A33, #0B1220);
            color: #F0F4FC;
            border-radius: 12px;
            border: 1px solid rgba(255, 255, 255, 0.08);
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.35),
                        0 12px 32px -16px rgba(0, 0, 0, 0.60);
        }
        .no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
        .no-scrollbar::-webkit-scrollbar { width: 0; height: 0; display: none; }
        .arena-lift {
            transition: transform 200ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 200ms cubic-bezier(0.16, 1, 0.3, 1);
            will-change: transform;
        }
        .arena-lift:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.40), 0 16px 40px -8px rgba(0, 0, 0, 0.55);
        }
        .arena-table-row:hover { background: rgba(255, 255, 255, 0.03); }
        .arena-table-row { min-height: 36px; }

        /* ── App shell height: dvh so mobile browser chrome doesn't eat the bottom ── */
        .na-shell { height: 100vh; height: 100dvh; }

        /* ── Mobile horizontal scroll wrapper for tables ─────────────────── */
        .na-table-scroll {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
        }
        

        /* ── Mobile global overrides (<920px) ───────────────────────────── */
        @media (max-width: 920px) {
            .na-table-scroll > * { min-width: 560px; }

            /* Страница не скроллится вбок: широкие таблицы скроллятся внутри
               своего .na-table-scroll, остальное обязано влезать в экран. */
            .na-scrollport { overflow-x: hidden !important; }

            /* Поиск: инлайновый min-width 300px распирал шапки страниц */
            .na-search-input {
                min-width: 0 !important;
                width: 100% !important;
                flex: 1 1 100% !important;
            }
            /* Content padding tighter on mobile */
            .na-main .na-page-content {
                padding-left: 14px !important;
                padding-right: 14px !important;
                padding-top: 20px !important;
                padding-bottom: 20px !important;
            }

            /* Rail hidden — main column takes full width automatically (flex-grow:1 via weight).
               No padding-bottom compensation needed (no bottom bar). */

            /* Dashboard hero row: stack vertically on mobile */
            .na-hero-row {
                flex-wrap: wrap !important;
                gap: 12px !important;
            }
            .na-hero-right {
                flex-wrap: wrap !important;
                width: 100% !important;
            }

            /* Analytics / Inbox tab switcher: horizontal scroll */
            .na-tab-switcher {
                overflow-x: auto !important;
                -webkit-overflow-scrolling: touch !important;
                scrollbar-width: none !important;
                flex-shrink: 0;
            }
            .na-tab-switcher::-webkit-scrollbar { display: none; }

            /* Page header row on mobile: wrap to avoid title + switcher clash */
            .na-page-header {
                flex-wrap: wrap !important;
                gap: 12px !important;
            }

            /* Topbar: hide subtitle on mobile, keep title compact */
            .na-topbar-subtitle { display: none !important; }

            /* Desktop topbar right section: hidden on mobile (hamburger replaces nav) */
            .na-desktop-topbar-right { display: none !important; }

            /* Hamburger button + mobile avatar: visible on mobile */
            .na-mobile-hamburger { display: flex !important; align-items: center !important; }
            .na-mobile-avatar-chip { display: flex !important; }

            /* Topbar left padding tighter on mobile */
            .na-main > div:first-child > div:first-child {
                padding-left: 14px !important;
                padding-right: 14px !important;
                padding-top: 8px !important;
                padding-bottom: 8px !important;
            }

            /* CmdK chip hidden on mobile to save topbar space */
            .na-cmdk-chip { display: none !important; }

            /* Drawer: full-width on narrow screens */
            .na-drawer-panel {
                width: 100% !important;
                max-width: 100% !important;
            }

            /* Command palette: 92vw */
            .na-cmd-palette {
                width: 92vw !important;
                max-width: 92vw !important;
            }

            /* Touch targets: tab chips + period chips minimum 40px */
            .na-tab-chip, .na-period-chip, .na-rail-btn {
                min-height: 40px !important;
                min-width: 40px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
            }

            /* Blob decorations: smaller on mobile for perf */
            .na-scene i:nth-of-type(1) {
                width: 260px !important; height: 260px !important;
                left: -80px !important; top: -80px !important;
            }
            .na-scene i:nth-of-type(2) {
                width: 200px !important; height: 200px !important;
            }
            .na-scene i:nth-of-type(3) {
                width: 220px !important; height: 220px !important;
            }
        }

        /* ── Desktop: hide mobile-only elements ─────────────────────────── */
        @media (min-width: 921px) {
            .na-mobile-hamburger { display: none !important; }
            .na-mobile-avatar-chip { display: none !important; }
        }

        /* ── People / clubs card grid: allow 160px min on mobile ────────── */
        @media (max-width: 600px) {
            .na-card-grid {
                grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)) !important;
            }
            /* Drawer full-width already via na-drawer-panel; reinforce */
            .na-drawer-panel { left: 0 !important; }
        }

        /* ── Inbox chat: mobile single-pane behaviour (<1000px) ─────────── */
        @media (max-width: 1000px) {
            /* Show «← Назад» button in thread pane */
            .na-chat-back-btn { display: flex !important; }
            /* Hide list when thread is active */
            .na-chat-list-hide { display: none !important; }
            /* Hide thread when list is shown */
            .na-chat-thread-hide { display: none !important; }
            /* Thread pane takes full width in single-pane mode */
            .arena-chat-grid .na-chat-thread-show { grid-column: 1 / -1; }
            .arena-chat-grid .na-chat-list-show { grid-column: 1 / -1; }
        }
        @keyframes arena-scrim-in {
            from { opacity: 0; }
            to   { opacity: 1; }
        }
        @keyframes arena-scrim-out {
            from { opacity: 1; }
            to   { opacity: 0; }
        }
        @keyframes arena-drawer-in {
            from { transform: translateX(100%); }
            to   { transform: translateX(0); }
        }
        @keyframes arena-drawer-out {
            from { transform: translateX(0); }
            to   { transform: translateX(100%); }
        }
    """.trimIndent()
    document.head?.appendChild(styleEl)

    ctx.stylesheet.registerStyleBase(":root") {
        Modifier.styleModifier {
            // Night Arena renders 1:1 — the legacy scaled-canvas (min(vw/1920, vh/1080))
            // shrank every s()-sized element to ~65-75% on laptops (tiny fonts,
            // mixed-scale overlaps with raw-px components). Kept as a var so the
            // many s() call sites keep working, but pinned to design size.
            property("--scale", "1px")
        }
    }

    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
            .fontSize(16.px)
            .fontWeight(AppTypography.Regular)
            .margin(0.px)
            .padding(0.px)
            .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden)
            .styleModifier {
                property("background-color", "#05080F")
                property("color", "#F0F4FC")
                property("height", "100vh")   // фолбэк для браузеров без dvh
                property("height", "100dvh")
                property("-webkit-font-smoothing", "antialiased")
                property("-moz-osx-font-smoothing", "grayscale")
            }
    }

    ctx.stylesheet.registerStyleBase("html") {
        Modifier
            .backgroundColor(AppColors.Dark.Background)
            .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden)
            .styleModifier { property("height", "100vh")   // фолбэк для браузеров без dvh
                property("height", "100dvh") }
    }

    ctx.stylesheet.registerStyleBase(".silk-surface") {
        Modifier.styleModifier {
            property("background-color", "transparent !important")
        }
    }

    ctx.stylesheet.registerStyleBase("input::placeholder, textarea::placeholder") {
        Modifier.styleModifier {
            property("color", "rgba(240, 244, 252, 0.38)")
            property("opacity", "1")
        }
    }

    ctx.stylesheet.registerStyleBase("body") {
        Modifier.styleModifier {
            property("user-select", "none")
            property("-webkit-user-select", "none")
            property("-webkit-touch-callout", "none")
        }
    }
    ctx.stylesheet.registerStyleBase("input, textarea, [contenteditable=\"true\"], [contenteditable=\"\"]") {
        Modifier.styleModifier {
            property("user-select", "text")
            property("-webkit-user-select", "text")
        }
    }
    ctx.stylesheet.registerStyleBase("img") {
        Modifier.styleModifier {
            property("-webkit-user-drag", "none")
            property("user-drag", "none")
        }
    }
}

private var errorOverlayInstalled = false

private fun installGlobalErrorOverlay() {
    if (errorOverlayInstalled) return
    errorOverlayInstalled = true

    val show: (String) -> Unit = { details ->
        try {
            val existing = document.getElementById("arena-error-overlay")
            if (existing != null) existing.remove()
            val overlay = document.createElement("div") as org.w3c.dom.HTMLDivElement
            overlay.id = "arena-error-overlay"
            overlay.setAttribute(
                "style",
                "position:fixed;top:0;left:0;right:0;z-index:99999;" +
                    "background:#FBEAE7;color:#D05C46;" +
                    "font-family:'JetBrains Mono',monospace;font-size:12px;" +
                    "padding:14px 20px;border-bottom:1px solid rgba(208,92,70,0.30);" +
                    "max-height:50vh;overflow:auto;white-space:pre-wrap;" +
                    "box-shadow:0 8px 28px rgba(15,18,34,0.10)"
            )
            overlay.innerHTML =
                "<div style='display:flex;align-items:center;gap:10px;margin-bottom:8px;'>" +
                    "<span style='color:#D05C46;font-weight:700;letter-spacing:0.18em;'>RUNTIME ERROR</span>" +
                    "<button id='arena-error-dismiss' " +
                    "style='margin-left:auto;background:#FBEAE7;color:#D05C46;" +
                    "border:1px solid rgba(208,92,70,0.30);border-radius:8px;padding:4px 10px;" +
                    "font-family:inherit;font-size:11px;cursor:pointer;letter-spacing:0.12em;'>DISMISS</button>" +
                    "<button id='arena-error-reset' " +
                    "style='background:rgba(11,171,100,0.12);color:#089A57;" +
                    "border:1px solid rgba(11,171,100,0.30);border-radius:8px;padding:4px 10px;" +
                    "font-family:inherit;font-size:11px;cursor:pointer;letter-spacing:0.12em;'>CLEAR SESSION &amp; RELOAD</button>" +
                    "</div>" +
                    "<div>${escapeHtml(details)}</div>"
            document.body?.appendChild(overlay)
            document.getElementById("arena-error-dismiss")?.addEventListener("click", { overlay.remove() })
            document.getElementById("arena-error-reset")?.addEventListener("click", {
                try { kotlinx.browser.localStorage.clear() } catch (_: Throwable) {}
                window.location.reload()
            })
        } catch (_: Throwable) {}
    }

    window.addEventListener("error", { event ->
        val e = event.asDynamic()
        val msg = (e.message as? String) ?: e.toString()
        val src = (e.filename as? String) ?: ""
        val line = (e.lineno as? Number)?.toString() ?: ""
        val col = (e.colno as? Number)?.toString() ?: ""
        val stack = (e.error?.stack as? String) ?: ""
        show("$msg\n\n$src:$line:$col\n\n$stack")
        console.error("Arena overlay caught:", e)
    })

    window.addEventListener("unhandledrejection", { event ->
        val r = event.asDynamic().reason
        val msg = (r?.message as? String) ?: r?.toString() ?: "Unhandled promise rejection"
        val stack = (r?.stack as? String) ?: ""
        show("$msg\n\n$stack")
        console.error("Arena overlay caught (promise):", r)
    })
}

private fun escapeHtml(s: String): String =
    s.replace("&", "&amp;").replace("<", "&lt;").replace(">", "&gt;")
